import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../hooks/use-auth";
import { useToast } from "../hooks/use-toast";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  ArrowLeft, Save, User, Medal, FileText, Plus, Trash2,
  AlertTriangle, CheckCircle2, Clock, Pencil, X, Activity, Lock,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatDate(d) {
  if (!d) return "—";
  try { return new Date(d + "T00:00:00").toLocaleDateString("pt-PT"); } catch { return d; }
}

function formatSpecialties(raw) {
  if (!raw) return "—";
  try {
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr) || arr.length === 0) return "—";
    return arr.join(", ");
  } catch {
    return raw;
  }
}

export default function PerfilPage() {
  const { token } = useAuth();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "profile";

  const [athlete, setAthlete] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);

  // Form state
  const [form, setForm] = useState({});
  const [guardianForm, setGuardianForm] = useState({});

  // PB form
  const [newPb, setNewPb] = useState({ event: "", mark: "", date: "", location: "" });
  const [addingPb, setAddingPb] = useState(false);

  // Doc form
  const [newDoc, setNewDoc] = useState({ type: "medical", filename: "", file_url: "", expiry_date: "" });
  const [addingDoc, setAddingDoc] = useState(false);

  // Password form
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [changingPw, setChangingPw] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${API}/athletes/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAthlete(res.data);
        setForm({
          name: res.data.name || "",
          phone: res.data.phone || "",
          address: res.data.address || "",
          nif: res.data.nif || "",
          birth_date: res.data.birth_date || "",
          gender: res.data.gender || "",
          codigo_postal: res.data.codigo_postal || "",
          localidade: res.data.localidade || "",
          documento_identificacao: res.data.documento_identificacao || "",
          numero_identificacao: res.data.numero_identificacao || "",
        });
        if (res.data.guardian) {
          setGuardianForm({
            name: res.data.guardian.name || "",
            email: res.data.guardian.email || "",
            phone: res.data.guardian.phone || "",
            relation: res.data.guardian.relation || "",
          });
        }
      } catch {
        toast({ title: "Erro", description: "Não foi possível carregar o perfil.", variant: "destructive" });
      } finally {
        setFetching(false);
      }
    };
    fetchProfile();
  }, [token, toast]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const [k, v] of Object.entries(form)) {
        if (v !== (athlete[k] || "")) payload[k] = v;
      }
      if (athlete.guardian || guardianForm.name) {
        payload.guardian = guardianForm;
      }

      const res = await axios.put(`${API}/athletes/me`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAthlete(res.data);
      setEditing(false);
      toast({ title: "Perfil atualizado", description: "Os teus dados foram guardados com sucesso." });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Erro ao guardar.";
      toast({ title: "Erro", description: detail, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleAddPb = async () => {
    if (!newPb.event || !newPb.mark || !newPb.date) {
      toast({ title: "Campos em falta", description: "Preenche evento, marca e data.", variant: "destructive" });
      return;
    }
    setAddingPb(true);
    try {
      const res = await axios.post(`${API}/athletes/me/personal-bests`, newPb, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAthlete((prev) => ({ ...prev, personal_bests: [...prev.personal_bests, res.data] }));
      setNewPb({ event: "", mark: "", date: "", location: "" });
      toast({ title: "Recorde adicionado!" });
    } catch {
      toast({ title: "Erro", description: "Não foi possível adicionar o recorde.", variant: "destructive" });
    } finally {
      setAddingPb(false);
    }
  };

  const handleDeletePb = async (pbId) => {
    try {
      await axios.delete(`${API}/athletes/me/personal-bests/${pbId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAthlete((prev) => ({
        ...prev,
        personal_bests: prev.personal_bests.filter((pb) => pb.id !== pbId),
      }));
      toast({ title: "Recorde removido." });
    } catch {
      toast({ title: "Erro", description: "Não foi possível remover o recorde.", variant: "destructive" });
    }
  };

  const handleAddDoc = async () => {
    if (!newDoc.filename) {
      toast({ title: "Campos em falta", description: "Preenche o nome do ficheiro.", variant: "destructive" });
      return;
    }
    setAddingDoc(true);
    try {
      const res = await axios.post(`${API}/athletes/me/documents`, newDoc, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAthlete((prev) => ({ ...prev, documents: [...prev.documents, res.data] }));
      setNewDoc({ type: "medical", filename: "", file_url: "", expiry_date: "" });
      toast({ title: "Documento adicionado!" });
    } catch {
      toast({ title: "Erro", description: "Não foi possível adicionar o documento.", variant: "destructive" });
    } finally {
      setAddingDoc(false);
    }
  };

  const handleDeleteDoc = async (docId) => {
    try {
      await axios.delete(`${API}/athletes/me/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAthlete((prev) => ({
        ...prev,
        documents: prev.documents.filter((d) => d.id !== docId),
      }));
      toast({ title: "Documento removido." });
    } catch {
      toast({ title: "Erro", description: "Não foi possível remover o documento.", variant: "destructive" });
    }
  };

  const handleChangePassword = async () => {
    if (!pwForm.current_password) {
      toast({ title: "Campos em falta", description: "Indica a password atual.", variant: "destructive" });
      return;
    }
    if ((pwForm.new_password || "").length < 6) {
      toast({ title: "Password inválida", description: "A nova password deve ter pelo menos 6 caracteres.", variant: "destructive" });
      return;
    }
    if (pwForm.new_password !== pwForm.confirm_password) {
      toast({ title: "Passwords não coincidem", description: "A confirmação não coincide com a nova password.", variant: "destructive" });
      return;
    }
    setChangingPw(true);
    try {
      await axios.post(`${API}/athletes/me/change-password`, {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPwForm({ current_password: "", new_password: "", confirm_password: "" });
      toast({ title: "Password alterada", description: "A tua password foi alterada com sucesso." });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Erro ao alterar a password.";
      toast({ title: "Erro", description: detail, variant: "destructive" });
    } finally {
      setChangingPw(false);
    }
  };

  if (fetching) {
    return (
      <main className="pt-32 pb-20 bg-stone-50 min-h-screen">
        <div className="max-w-3xl mx-auto px-6 flex items-center justify-center min-h-[40vh]">
          <div className="w-10 h-10 border-4 border-brand-200 border-t-brand-700 rounded-full animate-spin" />
        </div>
      </main>
    );
  }

  if (!athlete) {
    return (
      <main className="pt-32 pb-20 bg-stone-50 min-h-screen">
        <div className="max-w-3xl mx-auto px-6 text-center py-20">
          <p className="text-stone-500">Perfil não encontrado.</p>
          <Link to="/area-socio/dashboard" className="text-brand-700 font-semibold mt-2 inline-block">Voltar</Link>
        </div>
      </main>
    );
  }

  const tabs = [
    { id: "profile", label: "Dados Pessoais", icon: User },
    ...(athlete.is_atleta ? [{ id: "athlete", label: "Atleta", icon: Activity }] : []),
    { id: "records", label: "Recordes Pessoais", icon: Medal },
    { id: "docs", label: "Documentos", icon: FileText },
  ];

  return (
    <main className="pt-32 pb-20 bg-stone-50 min-h-screen">
      <div className="max-w-3xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Link
            to="/area-socio/dashboard"
            className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-brand-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Dashboard
          </Link>
          <span className="text-xs bg-brand-50 text-brand-700 px-2 py-0.5 rounded-full font-medium">
            Sócio {athlete.member_number}
          </span>
        </div>

        <h1 className="text-3xl font-bold text-stone-900 mb-2">{athlete.name}</h1>
        <p className="text-stone-500 mb-8">{athlete.email}</p>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white rounded-xl p-1 border border-stone-100 shadow-sm">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <Link
                key={tab.id}
                to={`?tab=${tab.id}`}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? "bg-brand-700 text-white shadow"
                    : "text-stone-500 hover:text-stone-700 hover:bg-stone-50"
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </Link>
            );
          })}
        </div>

        {/* Tab: Profile */}
        {activeTab === "profile" && (
          <div className="space-y-6">
            {/* Minor warning */}
            {athlete.is_minor && (
              <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800">
                <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-sm">Atleta menor de idade</p>
                  <p className="text-xs mt-0.5">Os dados do Encarregado de Educação são obrigatórios.</p>
                </div>
              </div>
            )}

            {/* Edit / Save toolbar */}
            <div className="flex justify-end gap-2">
              {!editing ? (
                <Button onClick={() => setEditing(true)} variant="outline" className="flex items-center gap-2">
                  <Pencil className="w-4 h-4" /> Editar
                </Button>
              ) : (
                <>
                  <Button onClick={() => setEditing(false)} variant="ghost" className="text-stone-500">
                    <X className="w-4 h-4 mr-1" /> Cancelar
                  </Button>
                  <Button onClick={handleSaveProfile} disabled={saving} className="bg-brand-700 hover:bg-brand-800 text-white">
                    <Save className="w-4 h-4 mr-1" />
                    {saving ? "A guardar..." : "Guardar"}
                  </Button>
                </>
              )}
            </div>

            {/* Personal data card */}
            <div className="bg-white rounded-2xl shadow-xl border border-stone-100 p-8">
              <h2 className="text-lg font-bold text-stone-900 mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-brand-600" /> Dados Pessoais
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Nome" value={form.name} editing={editing} onChange={(v) => setForm({ ...form, name: v })} />
                <Field label="Email" value={athlete.email} editing={false} />
                <Field label="Data de Nascimento" value={form.birth_date} editing={editing} type="date" onChange={(v) => setForm({ ...form, birth_date: v })} />
                <Field label="Idade" value={`${athlete.age} anos`} editing={false} />
                <Field label="Género" value={form.gender === "M" ? "Masculino" : form.gender === "F" ? "Feminino" : form.gender || "—"} editing={false} />
                <Field label="Telefone" value={form.phone} editing={editing} onChange={(v) => setForm({ ...form, phone: v })} />
                <Field label="Morada" value={form.address} editing={editing} onChange={(v) => setForm({ ...form, address: v })} />
                <Field label="Código Postal" value={form.codigo_postal} editing={editing} onChange={(v) => setForm({ ...form, codigo_postal: v })} />
                <Field label="Localidade" value={form.localidade} editing={editing} onChange={(v) => setForm({ ...form, localidade: v })} />
                <Field label="NIF" value={form.nif} editing={false} />
                <Field label="Doc. Identificação" value={form.documento_identificacao} editing={false} />
                <Field label="Nº Identificação" value={form.numero_identificacao} editing={false} />
              </div>
            </div>

            {/* Guardian card */}
            <div className="bg-white rounded-2xl shadow-xl border border-stone-100 p-8">
              <h2 className="text-lg font-bold text-stone-900 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" /> Encarregado de Educação
              </h2>
              {guardianForm.name ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Nome" value={guardianForm.name} editing={editing} onChange={(v) => setGuardianForm({ ...guardianForm, name: v })} />
                  <Field label="Email" value={guardianForm.email} editing={editing} onChange={(v) => setGuardianForm({ ...guardianForm, email: v })} />
                  <Field label="Telefone" value={guardianForm.phone} editing={editing} onChange={(v) => setGuardianForm({ ...guardianForm, phone: v })} />
                  <Field label="Relação" value={guardianForm.relation} editing={editing} onChange={(v) => setGuardianForm({ ...guardianForm, relation: v })} />
                </div>
              ) : (
                <p className="text-sm text-stone-400">Nenhum encarregado de educação registado.</p>
              )}
            </div>

            {/* Change password card */}
            <div className="bg-white rounded-2xl shadow-xl border border-stone-100 p-8">
              <h2 className="text-lg font-bold text-stone-900 mb-4 flex items-center gap-2">
                <Lock className="w-5 h-5 text-brand-600" /> Alterar Password
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-medium text-stone-500 block mb-1">Password atual</label>
                  <Input type="password" value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} placeholder="••••••••" />
                </div>
                <div>
                  <label className="text-xs font-medium text-stone-500 block mb-1">Nova password</label>
                  <Input type="password" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} placeholder="Mín. 6 caracteres" />
                </div>
                <div>
                  <label className="text-xs font-medium text-stone-500 block mb-1">Confirmar nova password</label>
                  <Input type="password" value={pwForm.confirm_password} onChange={(e) => setPwForm({ ...pwForm, confirm_password: e.target.value })} placeholder="••••••••" />
                </div>
              </div>
              <div className="mt-4">
                <Button onClick={handleChangePassword} disabled={changingPw} className="bg-brand-700 hover:bg-brand-800 text-white">
                  {changingPw ? "A alterar..." : "Alterar Password"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Tab: Atleta */}
        {activeTab === "athlete" && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-xl border border-stone-100 p-8">
              <h2 className="text-lg font-bold text-stone-900 mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-brand-600" /> Dados de Atleta
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Modalidade" value={athlete.modality || "—"} editing={false} />
                <Field label="Escalão" value={athlete.escalao || "—"} editing={false} />
                <Field label="Nº de Federado" value={athlete.num_fed || "—"} editing={false} />
                <Field label="Treinador" value={athlete.coach || "—"} editing={false} />
                <Field label="Data de Inscrição" value={athlete.data_socio ? formatDate(athlete.data_socio) : "—"} editing={false} />
              </div>
            </div>
          </div>
        )}

        {/* Tab: Records */}
        {activeTab === "records" && (
          <div className="space-y-6">
            {/* Add PB form */}
            <div className="bg-white rounded-2xl shadow-xl border border-stone-100 p-6">
              <h2 className="text-lg font-bold text-stone-900 mb-4 flex items-center gap-2">
                <Plus className="w-5 h-5 text-ember-500" /> Adicionar Recorde
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                <div>
                  <label className="text-xs font-medium text-stone-500 block mb-1">Evento *</label>
                  <Input value={newPb.event} onChange={(e) => setNewPb({ ...newPb, event: e.target.value })} placeholder="Ex: 100m" />
                </div>
                <div>
                  <label className="text-xs font-medium text-stone-500 block mb-1">Marca *</label>
                  <Input value={newPb.mark} onChange={(e) => setNewPb({ ...newPb, mark: e.target.value })} placeholder="Ex: 11.2s" />
                </div>
                <div>
                  <label className="text-xs font-medium text-stone-500 block mb-1">Data *</label>
                  <Input type="date" value={newPb.date} onChange={(e) => setNewPb({ ...newPb, date: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-medium text-stone-500 block mb-1">Local</label>
                  <Input value={newPb.location} onChange={(e) => setNewPb({ ...newPb, location: e.target.value })} placeholder="Ex: Estádio Nacional" />
                </div>
              </div>
              <Button onClick={handleAddPb} disabled={addingPb} className="bg-ember-500 hover:bg-ember-600 text-white">
                <Plus className="w-4 h-4 mr-1" />
                {addingPb ? "A adicionar..." : "Adicionar Recorde"}
              </Button>
            </div>

            {/* PB list */}
            <div className="bg-white rounded-2xl shadow-xl border border-stone-100 p-6">
              <h2 className="text-lg font-bold text-stone-900 mb-4 flex items-center gap-2">
                <Medal className="w-5 h-5 text-ember-500" /> Recordes Pessoais
              </h2>
              {athlete.personal_bests && athlete.personal_bests.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-stone-100 text-left text-stone-500">
                        <th className="pb-3 font-medium">Evento</th>
                        <th className="pb-3 font-medium">Marca</th>
                        <th className="pb-3 font-medium">Data</th>
                        <th className="pb-3 font-medium">Local</th>
                        <th className="pb-3 font-medium w-12"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {athlete.personal_bests.map((pb) => (
                        <tr key={pb.id} className="border-b border-stone-50">
                          <td className="py-3 font-medium text-stone-900">{pb.event}</td>
                          <td className="py-3 text-ember-600 font-bold">{pb.mark}</td>
                          <td className="py-3 text-stone-500">{pb.date}</td>
                          <td className="py-3 text-stone-500">{pb.location || "—"}</td>
                          <td className="py-3">
                            <button
                              onClick={() => handleDeletePb(pb.id)}
                              className="text-red-400 hover:text-red-600 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Medal className="w-10 h-10 text-stone-200 mx-auto mb-2" />
                  <p className="text-stone-400 text-sm">Nenhum recorde pessoal registado.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab: Documents */}
        {activeTab === "docs" && (
          <div className="space-y-6">
            {/* Add doc form */}
            <div className="bg-white rounded-2xl shadow-xl border border-stone-100 p-6">
              <h2 className="text-lg font-bold text-stone-900 mb-4 flex items-center gap-2">
                <Plus className="w-5 h-5 text-brand-600" /> Adicionar Documento
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="text-xs font-medium text-stone-500 block mb-1">Tipo *</label>
                  <select
                    value={newDoc.type}
                    onChange={(e) => setNewDoc({ ...newDoc, type: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-stone-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  >
                    <option value="medical">Ficha Médica</option>
                    <option value="insurance">Seguro</option>
                    <option value="guardian_consent">Autorização EE</option>
                    <option value="id">Documento Identificação</option>
                    <option value="other">Outro</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-stone-500 block mb-1">Nome do ficheiro *</label>
                  <Input value={newDoc.filename} onChange={(e) => setNewDoc({ ...newDoc, filename: e.target.value })} placeholder="Ex: ficha_medica.pdf" />
                </div>
                <div>
                  <label className="text-xs font-medium text-stone-500 block mb-1">URL (opcional)</label>
                  <Input value={newDoc.file_url} onChange={(e) => setNewDoc({ ...newDoc, file_url: e.target.value })} placeholder="https://..." />
                </div>
                <div>
                  <label className="text-xs font-medium text-stone-500 block mb-1">Data de validade</label>
                  <Input type="date" value={newDoc.expiry_date} onChange={(e) => setNewDoc({ ...newDoc, expiry_date: e.target.value })} />
                </div>
              </div>
              <Button onClick={handleAddDoc} disabled={addingDoc} className="bg-brand-700 hover:bg-brand-800 text-white">
                <Plus className="w-4 h-4 mr-1" />
                {addingDoc ? "A adicionar..." : "Adicionar Documento"}
              </Button>
            </div>

            {/* Docs list */}
            <div className="bg-white rounded-2xl shadow-xl border border-stone-100 p-6">
              <h2 className="text-lg font-bold text-stone-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-brand-600" /> Documentos
              </h2>
              {athlete.documents && athlete.documents.length > 0 ? (
                <div className="space-y-3">
                  {athlete.documents.map((doc) => {
                    const isExpired = doc.expiry_date && new Date(doc.expiry_date) < new Date();
                    return (
                      <div key={doc.id} className="flex items-center justify-between p-4 rounded-xl border border-stone-100 hover:border-brand-200 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            isExpired ? "bg-red-50 text-red-500" : "bg-brand-50 text-brand-600"
                          }`}>
                            {isExpired ? <Clock className="w-5 h-5" /> : <CheckCircle2 className="w-5 h-5" />}
                          </div>
                          <div>
                            <p className="font-medium text-stone-900 text-sm">{doc.filename}</p>
                            <div className="flex gap-2 mt-0.5">
                              <span className="text-xs bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full">
                                {doc.type === "medical" ? "Ficha Médica" :
                                 doc.type === "insurance" ? "Seguro" :
                                 doc.type === "guardian_consent" ? "Autorização EE" :
                                 doc.type === "id" ? "Identificação" : doc.type}
                              </span>
                              {doc.expiry_date && (
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  isExpired ? "bg-red-50 text-red-600" : "bg-stone-100 text-stone-500"
                                }`}>
                                  Validade: {doc.expiry_date}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteDoc(doc.id)}
                          className="text-red-400 hover:text-red-600 transition-colors shrink-0 ml-2"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-10 h-10 text-stone-200 mx-auto mb-2" />
                  <p className="text-stone-400 text-sm">Nenhum documento registado.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}

// Reusable field component
function Field({ label, value, editing, onChange, type, options }) {
  return (
    <div>
      <label className="text-xs font-medium text-stone-500 block mb-1">{label}</label>
      {editing && onChange ? (
        type === "select" ? (
          <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full h-10 px-3 rounded-md border border-stone-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          >
            {(options || []).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        ) : (
          <Input
            type={type || "text"}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        )
      ) : (
        <p className="text-sm text-stone-700 min-h-[1.5rem] break-words">
          {value || <span className="text-stone-300 italic">—</span>}
        </p>
      )}
    </div>
  );
}
